// import hooks
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

// import types
import { GridColDef } from '@mui/x-data-grid-pro';
import { AdditionalCompensationResponse } from 'generated/index';

// import utils
import { dateAndTimeFormatter } from 'utils/grid';
import BooleanCell from 'components/shared/Table/TableCell/DataGrid/BooleanCell';

export const useColumnDefinitions = ({
  showEmployeeName = false
}: {
  showEmployeeName?: boolean;
}) => {
  const { t } = useTranslation('common');

  return useMemo(
    () =>
      [
        {
          field: 'employmentNumber',
          type: 'string',
          headerName: t('additionalCompensations.employmentNumber'),
          valueGetter: (_, row: AdditionalCompensationResponse) =>
            row?.employment?.number ?? '',
          width: 150
        },
        {
          field: 'employeeNumber',
          type: 'string',
          headerName: t('additionalCompensations.employeeNumber'),
          valueGetter: (_, row: AdditionalCompensationResponse) =>
            row?.employment?.employeeNumber ?? '',
          width: 150
        },
        ...(showEmployeeName
          ? [
              {
                field: 'employeeName',
                type: 'string',
                headerName: t('employee'),
                valueGetter: (_, row: AdditionalCompensationResponse) =>
                  row?.employment?.employeeName ?? '',
                flex: 1,
                minWidth: 150
              }
            ]
          : []),
        {
          field: 'organizationalUnitName',
          type: 'string',
          headerName: t('organizationalUnit'),
          valueGetter: (_, row: AdditionalCompensationResponse) =>
            row?.employment?.organizationalUnitName ?? '',
          flex: 1,
          minWidth: 150
        },
        {
          field: 'dutyStart',
          type: 'dateTime',
          headerName: t('additionalCompensations.dutyStart'),
          valueFormatter: dateAndTimeFormatter,
          width: 150
        },
        {
          field: 'dutyEnd',
          type: 'dateTime',
          headerName: t('additionalCompensations.dutyEnd'),
          valueFormatter: dateAndTimeFormatter,
          width: 150
        },
        {
          field: 'additionalCompensationType',
          type: 'string',
          headerName: t('additionalCompensations.type'),
          valueGetter: (_, row: AdditionalCompensationResponse) => {
            if (row?.additionalCompensationType === 'CREATED') {
              return t('additionalCompensations.created');
            }
            if (row?.additionalCompensationType === 'UPDATED') {
              return t('additionalCompensations.updated');
            }
            return row?.additionalCompensationType ?? '';
          },
          width: 150
        },
        {
          field: 'byWorkPattern',
          type: 'boolean',
          headerName: t('additionalCompensations.basedOnWorkPattern'),
          width: 180,
          renderCell: BooleanCell
        },
        {
          field: 'markedAsAdditionalCompensation',
          type: 'boolean',
          headerName: t(
            'additionalCompensations.markedAsAdditionalCompensation'
          ),
          width: 180,
          renderCell: BooleanCell
        },
        {
          field: 'mutatedOn',
          type: 'dateTime',
          headerName: t('additionalCompensations.mutatedOn'),
          valueFormatter: dateAndTimeFormatter,
          width: 180
        },
        {
          field: 'mutatedBy',
          type: 'string',
          headerName: t('additionalCompensations.mutatedBy'),
          width: 150
        }
      ] as GridColDef[],
    [t, showEmployeeName]
  );
};

export default useColumnDefinitions;
